/**
 * @fileoverview "Select an element" mode for a rendered report (MOR-924).
 *
 * A Cursor-style picking overlay: while active, hovering the report outlines
 * the block under the pointer and labels it ("Paragraph", "Table row", …);
 * clicking picks it as the target for the assistant's next edit, so the user
 * can aim at one paragraph or row rather than a whole section. Escape or a
 * click outside cancels.
 *
 * The overlay is drawn in a portal with fixed positioning from the hovered
 * element's client rect, so it never mutates the report DOM (which TipTap
 * owns) and can't perturb the layout it is outlining.
 */

import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  buildSelection,
  describeElement,
  selectableFrom,
  type ReportSelection,
} from "./reportSelection";

/** Class marking the element a pending selection refers to. */
export const SELECTED_ELEMENT_CLASS = "report-element-selected";

interface HoverState {
  top: number;
  left: number;
  width: number;
  height: number;
  label: string;
}

export function ReportElementPicker({
  active,
  bodyRef,
  onPick,
  onCancel,
}: {
  active: boolean;
  /** The report pane the user picks within. */
  bodyRef: React.RefObject<HTMLDivElement | null>;
  onPick: (selection: ReportSelection, element: HTMLElement) => void;
  onCancel: () => void;
}) {
  const [hover, setHover] = useState<HoverState | null>(null);

  const clear = useCallback(() => setHover(null), []);

  useEffect(() => {
    const body = bodyRef.current;
    if (!active || !body) {
      setHover(null);
      return;
    }

    const show = (el: HTMLElement) => {
      const rect = el.getBoundingClientRect();
      setHover({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        label: describeElement(el),
      });
    };

    const onMove = (e: MouseEvent) => {
      const el = selectableFrom(e.target as Element | null, body);
      if (el) show(el);
      else setHover(null);
    };

    const onClick = (e: MouseEvent) => {
      const el = selectableFrom(e.target as Element | null, body);
      if (!el) return;
      // Swallow the click so picking never follows a citation link or moves
      // the editor caret.
      e.preventDefault();
      e.stopPropagation();
      onPick(buildSelection(el, body), el);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      }
    };

    body.addEventListener("mousemove", onMove);
    body.addEventListener("mouseleave", clear);
    // Capture phase: get in before the citation/link handlers on the content.
    body.addEventListener("click", onClick, true);
    window.addEventListener("keydown", onKeyDown);
    // The outline is positioned from client rects, so it must follow scrolling.
    window.addEventListener("scroll", clear, true);
    return () => {
      body.removeEventListener("mousemove", onMove);
      body.removeEventListener("mouseleave", clear);
      body.removeEventListener("click", onClick, true);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", clear, true);
    };
  }, [active, bodyRef, onPick, onCancel, clear]);

  // Crosshair over the whole report while picking.
  useEffect(() => {
    const body = bodyRef.current;
    if (!body || !active) return;
    body.style.cursor = "crosshair";
    return () => {
      body.style.cursor = "";
    };
  }, [active, bodyRef]);

  if (!active || !hover) return null;

  // Label sits above the box, or inside it when the box is at the very top.
  const labelAbove = hover.top > 24;
  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-50">
      <div
        className="absolute rounded-sm border-2 border-primary/70 bg-primary/5"
        style={{
          top: hover.top,
          left: hover.left,
          width: hover.width,
          height: hover.height,
        }}
      />
      <div
        className="absolute rounded-md bg-primary px-1.5 py-0.5 text-[11px] font-medium text-primary-foreground shadow-sm"
        style={{
          top: labelAbove ? hover.top - 22 : hover.top + 2,
          left: hover.left,
        }}
      >
        {hover.label} · click to select
      </div>
    </div>,
    document.body,
  );
}
